<div class="main-content">
	<div class="container-fluid">
		<mat-card class="feature-surface">
			<mat-card-header>
				<mat-card-title>Vendor-Backed Remittance Upload</mat-card-title>
				<mat-card-subtitle>
					Apply a sticker / processor settlement file against many customer invoices in one shot
				</mat-card-subtitle>
			</mat-card-header>
			<mat-card-content>

				<!-- Stepper indicator -->
				<div class="d-flex align-items-center mb-4" style="gap: 10px;">
					<app-status-pill [status]="step >= 1 ? 'settled' : 'neutral'" [label]="'1 · File'"></app-status-pill>
					<span class="text-muted">────</span>
					<app-status-pill [status]="step >= 2 ? 'settled' : 'neutral'" [label]="'2 · Mapping'"></app-status-pill>
					<span class="text-muted">────</span>
					<app-status-pill [status]="step >= 3 ? 'settled' : 'neutral'" [label]="'3 · Preview & post'"></app-status-pill>
				</div>

				<!-- STEP 1 — vendor + file -->
				<form [formGroup]="fileForm" *ngIf="step === 1">
					<div class="row">
						<div class="col-md-6">
							<label class="d-block text-muted mb-1" style="font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em;">Backing vendor</label>
							<app-vendor-auto-complete (vendor_selected)="onVendorPicked($event)"></app-vendor-auto-complete>
							<small *ngIf="fileForm.get('vendor_id').value">
								Selected: <strong>{{ fileForm.get('vendor_name').value || fileForm.get('vendor_id').value }}</strong>
							</small>
						</div>
					</div>

					<div class="feature-section text-center mt-4" style="border-style: dashed; padding: 32px;">
						<input type="file" #fileInput accept=".csv,.xls,.xlsx" (change)="onFilePicked($event)" style="display: none;">
						<i class="material-icons" style="font-size: 36px; color: var(--feature-accent);">cloud_upload</i>
						<p class="mt-2 mb-1">Drop CSV / XLSX file or</p>
						<button mat-stroked-button color="accent" type="button" (click)="fileInput.click()">Browse</button>
						<p class="mt-2 text-muted" *ngIf="fileName">Selected: <strong>{{ fileName }}</strong></p>
					</div>

					<div class="row mt-4">
						<div class="col-12 text-right">
							<button mat-flat-button color="accent" type="button"
								[disabled]="!fileForm.valid || !fileContentBase64"
								(click)="goToMapping()">Next: Mapping</button>
						</div>
					</div>
				</form>

				<!-- STEP 2 — column mapping -->
				<div *ngIf="step === 2">
					<p class="text-muted mb-3">{{ fileName }} — map your file's columns to our fields</p>

					<table class="feature-table">
						<thead>
							<tr>
								<th>Our field</th>
								<th>Their column</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>Order # / PO</td>
								<td>
									<mat-form-field appearance="outline" class="w-100">
										<mat-select [(value)]="mapping.order_ref">
											<mat-option [value]="''">— pick —</mat-option>
											<mat-option *ngFor="let c of availableColumns" [value]="c">{{ c }}</mat-option>
										</mat-select>
									</mat-form-field>
								</td>
							</tr>
							<tr>
								<td>Vendor reference</td>
								<td>
									<mat-form-field appearance="outline" class="w-100">
										<mat-select [(value)]="mapping.vendor_reference">
											<mat-option [value]="''">— pick —</mat-option>
											<mat-option *ngFor="let c of availableColumns" [value]="c">{{ c }}</mat-option>
										</mat-select>
									</mat-form-field>
								</td>
							</tr>
							<tr>
								<td>Vendor relief $</td>
								<td>
									<mat-form-field appearance="outline" class="w-100">
										<mat-select [(value)]="mapping.vendor_amount">
											<mat-option [value]="''">— pick —</mat-option>
											<mat-option *ngFor="let c of availableColumns" [value]="c">{{ c }}</mat-option>
										</mat-select>
									</mat-form-field>
								</td>
							</tr>
							<tr>
								<td>Commission $</td>
								<td>
									<mat-form-field appearance="outline" class="w-100">
										<mat-select [(value)]="mapping.commission_amount">
											<mat-option [value]="''">— pick —</mat-option>
											<mat-option *ngFor="let c of availableColumns" [value]="c">{{ c }}</mat-option>
										</mat-select>
									</mat-form-field>
								</td>
							</tr>
							<tr>
								<td>Vendor settlement #</td>
								<td>
									<mat-form-field appearance="outline" class="w-100">
										<mat-select [(value)]="mapping.settlement_id">
											<mat-option [value]="''">— pick —</mat-option>
											<mat-option *ngFor="let c of availableColumns" [value]="c">{{ c }}</mat-option>
										</mat-select>
									</mat-form-field>
								</td>
							</tr>
						</tbody>
					</table>

					<div class="row mt-3">
						<div class="col-12 d-flex justify-content-between">
							<button mat-stroked-button type="button" (click)="step = 1">Back</button>
							<button mat-flat-button color="accent" type="button"
								[disabled]="previewing" (click)="goToPreview()">
								{{ previewing ? 'Matching…' : 'Next: Preview' }}
							</button>
						</div>
					</div>
				</div>

				<!-- STEP 3 — preview + commit -->
				<div *ngIf="step === 3 && preview">
					<div class="feature-tile-row">
						<app-kpi-tile tone="settled" label="matched"
							[value]="preview.totals?.matched"
							[sub]="'Vendor relief $' + (preview.totals?.vendor_relief | number:'1.2-2')"></app-kpi-tile>
						<app-kpi-tile tone="partial" label="partial" [value]="preview.totals?.partial" sub="amount mismatch"></app-kpi-tile>
						<app-kpi-tile tone="failed" label="unmatched" [value]="preview.totals?.unmatched" sub="invoice not found"></app-kpi-tile>
						<app-kpi-tile tone="accent" label="commission"
							[value]="'$' + (preview.totals?.commission | number:'1.2-2')"
							sub="aggregate"></app-kpi-tile>
					</div>

					<form [formGroup]="commitForm" class="feature-section mt-3">
						<p class="feature-section__title">Posting setup</p>
						<div class="row">
							<div class="col-md-3">
								<mat-form-field appearance="outline" class="w-100">
									<mat-label>Payment method</mat-label>
									<mat-select formControlName="payment_type">
										<mat-option *ngFor="let m of payment_methods" [value]="m.paymentid">{{ m.paymentname }}</mat-option>
									</mat-select>
								</mat-form-field>
							</div>
							<div class="col-md-3">
								<mat-form-field appearance="outline" class="w-100">
									<mat-label>Commission / clearing GL</mat-label>
									<input matInput formControlName="gl_account">
								</mat-form-field>
							</div>
							<div class="col-md-2">
								<mat-form-field appearance="outline" class="w-100">
									<mat-label>Bank</mat-label>
									<mat-select formControlName="bankaccount">
										<mat-option *ngFor="let b of bankaccounts" [value]="b.accountcode">{{ b.bankaccountname }}</mat-option>
									</mat-select>
								</mat-form-field>
							</div>
							<div class="col-md-2">
								<mat-form-field appearance="outline" class="w-100">
									<mat-label>Currency</mat-label>
									<mat-select formControlName="currency">
										<mat-option *ngFor="let c of currencies" [value]="c.currabrev">{{ c.currabrev }}</mat-option>
									</mat-select>
								</mat-form-field>
							</div>
							<div class="col-md-2">
								<mat-form-field appearance="outline" class="w-100">
									<mat-label>Post date</mat-label>
									<input matInput type="date" formControlName="postdate">
								</mat-form-field>
							</div>
						</div>
					</form>

					<h5 class="mt-4">Matched · {{ preview.matched?.length }} rows</h5>
					<table class="feature-table">
						<thead>
							<tr>
								<th></th>
								<th>Order #</th>
								<th>Customer</th>
								<th class="is-num">Vendor $</th>
								<th class="is-num">Comm $</th>
								<th>Inv #</th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let r of preview.matched">
								<td><app-status-pill status="settled" icon="check"></app-status-pill></td>
								<td>{{ r.order_ref }}</td>
								<td>{{ r.customer_name }}</td>
								<td class="is-num">{{ r.vendor_amount | currency }}</td>
								<td class="is-num">{{ r.commission_amount | currency }}</td>
								<td>{{ r.invoice_transno }}</td>
							</tr>
							<tr *ngIf="!preview.matched?.length"><td colspan="6" class="text-center text-muted">none</td></tr>
						</tbody>
					</table>

					<h5 class="mt-4">Partial · {{ preview.partial?.length }} rows</h5>
					<table class="feature-table">
						<thead>
							<tr>
								<th></th>
								<th>Order #</th>
								<th>Customer</th>
								<th class="is-num">File total</th>
								<th class="is-num">Invoice total</th>
								<th>Reason</th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let r of preview.partial">
								<td><app-status-pill status="partial" icon="warning_amber"></app-status-pill></td>
								<td>{{ r.order_ref }}</td>
								<td>{{ r.customer_name }}</td>
								<td class="is-num">{{ (r.vendor_amount + r.commission_amount) | currency }}</td>
								<td class="is-num">{{ r.expected_total | currency }}</td>
								<td><small>{{ r.reason }}</small></td>
							</tr>
							<tr *ngIf="!preview.partial?.length"><td colspan="6" class="text-center text-muted">none</td></tr>
						</tbody>
					</table>

					<h5 class="mt-4">Unmatched · {{ preview.unmatched?.length }} rows</h5>
					<table class="feature-table">
						<thead>
							<tr>
								<th></th>
								<th>Order #</th>
								<th class="is-num">Vendor $</th>
								<th class="is-num">Comm $</th>
								<th>Reason</th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let r of preview.unmatched">
								<td><app-status-pill status="failed" icon="close"></app-status-pill></td>
								<td>{{ r.order_ref || '—' }}</td>
								<td class="is-num">{{ r.vendor_amount | currency }}</td>
								<td class="is-num">{{ r.commission_amount | currency }}</td>
								<td><small>{{ r.reason }}</small></td>
							</tr>
							<tr *ngIf="!preview.unmatched?.length"><td colspan="5" class="text-center text-muted">none</td></tr>
						</tbody>
					</table>

					<div class="row mt-4">
						<div class="col-12 d-flex justify-content-between align-items-center" style="gap: 12px;">
							<button mat-stroked-button type="button" (click)="step = 2">Back</button>
							<div>
								<button mat-stroked-button type="button" (click)="reset()" class="mr-2">Start over</button>
								<button mat-stroked-button color="accent" type="button" [disabled]="committing || !commitForm.valid"
									(click)="commit(true)" class="mr-2">
									Post matched only
								</button>
								<button mat-flat-button color="accent" type="button" [disabled]="committing || !commitForm.valid"
									(click)="commit(false)">
									{{ committing ? 'Posting…' : 'Post matched + partial' }}
								</button>
							</div>
						</div>
					</div>

					<!-- Result summary -->
					<div *ngIf="postingResult" class="feature-section mt-4">
						<p class="feature-section__title">Posting result</p>
						<p>
							<app-status-pill [status]="postingResult.success ? 'settled' : 'failed'"
								[label]="(postingResult.totals?.posted || 0) + ' posted, ' + (postingResult.totals?.failed || 0) + ' failed'">
							</app-status-pill>
						</p>
						<div *ngIf="postingResult.errors?.length">
							<p class="text-muted mt-2 mb-1" style="font-size: 12px; text-transform: uppercase;">Errors</p>
							<table class="feature-table">
								<tbody>
									<tr *ngFor="let e of postingResult.errors">
										<td>{{ e.debtorno }}</td>
										<td><small>{{ e.message }}</small></td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>

				</div>

			</mat-card-content>
		</mat-card>
	</div>
</div>
